import {
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
} from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { BASE_COLORS } from '../../../constants';

const ROWS_PER_PAGE = 5;

const PANEL_SX = {
    backgroundColor: BASE_COLORS.PRIMARY_MAIN,
    border: `1px solid ${BASE_COLORS.PRIMARY_MAIN}`,
    boxShadow: 'none',
    color: BASE_COLORS.WHITE,
};

const formatCellValue = (value, maximumFractionDigits = 1) => {
    const numericValue = Number(value);

    if (value === null || typeof value === 'undefined' || Number.isNaN(numericValue)) {
        return '-';
    }

    return numericValue.toLocaleString('hu-HU', { maximumFractionDigits });
};
const formatDateValue = (value, fallbackValue) => {
    if (!value) {
        return fallbackValue;
    }

    const parsedDate = parseISO(value);

    return isValid(parsedDate) ? format(parsedDate, 'yyyy.MM.dd.') : fallbackValue;
};
const getRowKey = (selectedDate, row) =>
    [
        selectedDate,
        row.grid_id ?? 'grid',
        row.date ?? 'date',
        row.daily_precip_mm ?? 'null',
        row.daily_tmax_c ?? 'null',
    ].join('-');

const DroughtWarningRainTable = ({ selectedDate, rows = [], loading = false }) => {
    const [page, setPage] = useState(0);

    useEffect(() => {
        setPage(0);
    }, [selectedDate, rows]);

    const visibleRows = useMemo(() => {
        const start = page * ROWS_PER_PAGE;

        return rows.slice(start, start + ROWS_PER_PAGE);
    }, [page, rows]);

    return (
        <TableContainer
            component={Paper}
            elevation={0}
            sx={{
                boxShadow: 'none',
                backgroundColor: 'transparent',
                overflowX: 'auto',
                overflowY: 'hidden',
                border: '1px solid',
                borderColor: 'rgba(194, 27, 23, 0.2)',
            }}
        >
            <Table
                size="small"
                sx={{
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                    '& .MuiTableCell-root': {
                        borderBottom: '1px solid',
                        borderColor: 'rgba(194, 27, 23, 0.16)',
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell
                            sx={{
                                ...PANEL_SX,
                                fontSize: { xs: 13, sm: 16, md: 18 },
                                fontWeight: 700,
                                px: { xs: 0.75, sm: 1, md: 2 },
                                py: 0.5,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Dátum
                        </TableCell>
                        <TableCell
                            sx={{
                                ...PANEL_SX,
                                fontSize: { xs: 13, sm: 16, md: 18 },
                                fontWeight: 700,
                                px: { xs: 0.75, sm: 1, md: 2 },
                                py: 0.5,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Hőmérséklet
                        </TableCell>
                        <TableCell
                            sx={{
                                ...PANEL_SX,
                                fontSize: { xs: 13, sm: 16, md: 18 },
                                fontWeight: 700,
                                px: { xs: 0.75, sm: 1, md: 2 },
                                py: 0.5,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Csapadék
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {visibleRows.map((row) => (
                        <TableRow key={getRowKey(selectedDate, row)}>
                            <TableCell
                                sx={{
                                    fontSize: { xs: 13, sm: 15, md: 17 },
                                    px: { xs: 0.75, sm: 1, md: 2 },
                                    py: 0.5,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {formatDateValue(row.date, selectedDate)}
                            </TableCell>
                            <TableCell
                                sx={{
                                    fontSize: { xs: 13, sm: 15, md: 17 },
                                    px: { xs: 0.75, sm: 1, md: 2 },
                                    py: 0.5,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {formatCellValue(row.daily_tmax_c)} °C
                            </TableCell>
                            <TableCell
                                sx={{
                                    fontSize: { xs: 13, sm: 15, md: 17 },
                                    px: { xs: 0.75, sm: 1, md: 2 },
                                    py: 0.5,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {formatCellValue(row.daily_precip_mm)} mm
                            </TableCell>
                        </TableRow>
                    ))}
                    {!loading && !visibleRows.length ? (
                        <TableRow>
                            <TableCell
                                colSpan={3}
                                sx={{
                                    fontSize: { xs: 13, sm: 15, md: 17 },
                                    py: 1.5,
                                    textAlign: 'center',
                                }}
                            >
                                Nincs megjeleníthető adat.
                            </TableCell>
                        </TableRow>
                    ) : null}
                    {loading ? (
                        <TableRow>
                            <TableCell colSpan={3} sx={{ py: 2, textAlign: 'center' }}>
                                <CircularProgress size={28} sx={{ color: BASE_COLORS.WHITE }} />
                            </TableCell>
                        </TableRow>
                    ) : null}
                </TableBody>
            </Table>
            <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={(_, newPage) => {
                    setPage(newPage);
                }}
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[ROWS_PER_PAGE]}
                onRowsPerPageChange={() => {}}
                sx={{
                    '& .MuiTablePagination-toolbar': {
                        minHeight: { xs: 40, sm: 52 },
                        px: { xs: 0.5, sm: 2 },
                    },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                        display: 'none',
                    },
                    '& .MuiTablePagination-displayedRows': {
                        fontSize: { xs: 12, sm: 14 },
                        m: 0,
                    },
                    '& .MuiTablePagination-actions': {
                        ml: { xs: 0.5, sm: 2 },
                    },
                }}
            />
        </TableContainer>
    );
};

export default DroughtWarningRainTable;
